import { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { logInWithEmailAndPassword } from "../API/firebase";
import MySnackbar from "./snackBar";
import { PillButton } from "./ui";
import { paths } from "../routes/paths";

const ERROR_MESSAGES: Record<string, string> = {
  "auth/user-not-found": "No account found for that email address.",
  "auth/invalid-email": "That email address is not valid.",
  "auth/wrong-password": "Wrong email or password.",
  "auth/invalid-credential": "Wrong email or password.",
  "auth/too-many-requests": "Too many attempts. Try again shortly.",
};

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email || !password) return;

    setBusy(true);
    try {
      await logInWithEmailAndPassword(email, password);
      navigate(paths.home);
    } catch (error: unknown) {
      // firebase.ts rethrows `err.code`. A lookup rather than a chain of ifs —
      // the previous version had a duplicated branch that always fell through to
      // the generic message.
      const code = typeof error === "string" ? error : "";
      setSnackbarMessage(ERROR_MESSAGES[code] ?? "Wrong email or password.");
      setOpenSnackbar(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <PillButton
          tone="amber"
          type="submit"
          fullWidth
          disabled={busy || !email || !password}
          sx={{ mt: 3, mb: 2 }}
        >
          {busy ? "Signing in…" : "Sign in"}
        </PillButton>

        <Link
          component={RouterLink}
          to={paths.forgotPassword}
          variant="body2"
          sx={{ fontWeight: 700 }}
        >
          Forgot password?
        </Link>
      </Box>

      <MySnackbar
        message={snackbarMessage}
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
      />
    </>
  );
}
